import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatCardModule } from '@angular/material/card';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';

import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';
import { TranslateModule } from '@ngx-translate/core';

import { IonicModule } from '@ionic/angular';

import { ComponentsModule, CoreUiModule } from '@ridango/components';
import { CoreModule } from '@ridango/core';
import { UiModule } from '@ridango/ui';

import { QRCodeModule } from 'angularx-qrcode';

import { AlertComponent } from './containers/alert/alert.component';
import { UiInlineAlertComponent } from './containers/ui-inline-alert/ui-inline-alert.component';
import { UiModalAlertComponent } from './containers/ui-modal-alert/ui-modal-alert.component';
import { NoInternetAlertComponent } from './containers/no-internet-alert/no-internet-alert.component';
import { AdditionalInformationComponent } from './containers/book-ride/additional-information/additional-information.component';
import { BookingSummaryComponent } from './containers/book-ride/booking-summary/booking-summary.component';
import { BookingTicketComponent } from './containers/book-ride/booking-ticket/booking-ticket.component';
import { PassengerSelectorComponent } from './containers/book-ride/passenger-selector/passenger-selector.component';
import { PayingSelectorComponent } from './containers/book-ride/paying-selector/paying-selector.component';
import { PlanRideComponent } from './containers/book-ride/plan-ride/plan-ride.component';
import { CardMessageRedirectComponent } from './containers/card-message-redirect/card-message-redirect.component';
import { CookieNotificationComponent } from './containers/cookie-notification/cookie-notification.component';
import { CustomerAccountChangeEmailAsideComponent } from './containers/customer-account/customer-account-change-email-aside/customer-account-change-email-aside.component';
import { CustomerAccountChangeEmailComponent } from './containers/customer-account/customer-account-change-email/customer-account-change-email.component';
import { CustomerAccountChangePasswordAsideComponent } from './containers/customer-account/customer-account-change-password-aside/customer-account-change-password-aside.component';
import { CustomerAccountChangePasswordComponent } from './containers/customer-account/customer-account-change-password/customer-account-change-password.component';
import { CustomerAccountDeleteSuccessComponent } from './containers/customer-account/customer-account-delete-success/customer-account-delete-success.component';
import { CustomerAccountDetailAsideComponent } from './containers/customer-account/customer-account-detail-aside/customer-account-detail-aside.component';
import { CustomerAccountDetailComponent } from './containers/customer-account/customer-account-detail/customer-account-detail.component';
import { CustomerAccountEditAsideComponent } from './containers/customer-account/customer-account-edit-aside/customer-account-edit-aside.component';
import { CustomerAccountEditComponent } from './containers/customer-account/customer-account-edit/customer-account-edit.component';
import { AccountEmailTicketsComponent } from './containers/customer-account/travelcards-and-tickets/account-email-tickets/account-email-tickets.component';
import { AccountRelatedCardTicketsComponent } from './containers/customer-account/travelcards-and-tickets/account-related-card-tickets/account-related-card-tickets.component';
import { AccountTicketsListComponent } from './containers/customer-account/travelcards-and-tickets/account-tickets-list/account-tickets-list.component';
import { IsicCardActivationAsideComponent } from './containers/customer-account/travelcards-and-tickets/isic-card-activation-aside/isic-card-activation-aside.component';
import { IsicCardActivationComponent } from './containers/customer-account/travelcards-and-tickets/isic-card-activation/isic-card-activation.component';
import { RelatedCardAddAndEditAsideComponent } from './containers/customer-account/travelcards-and-tickets/related-card-add-and-edit-aside/related-card-add-and-edit-aside.component';
import { RelatedCardAddAndEditComponent } from './containers/customer-account/travelcards-and-tickets/related-card-add-and-edit/related-card-add-and-edit.component';
import { RelatedCardDetailComponent } from './containers/customer-account/travelcards-and-tickets/related-card-detail/related-card-detail.component';
import { RelatedCardPersonalizeAsideComponent } from './containers/customer-account/travelcards-and-tickets/related-card-personalize-aside/related-card-personalize-aside.component';
import { RelatedCardPersonalizeComponent } from './containers/customer-account/travelcards-and-tickets/related-card-personalize/related-card-personalize.component';
import { RelatedCardsEmptyStateComponent } from './containers/customer-account/travelcards-and-tickets/related-cards-empty-state/related-cards-empty-state.component';
import { RelatedCardsListHeaderComponent } from './containers/customer-account/travelcards-and-tickets/related-cards-list-header/related-cards-list-header.component';
import { RelatedCardsListComponent } from './containers/customer-account/travelcards-and-tickets/related-cards-list/related-cards-list.component';
import { VoucherTicketsComponent } from './containers/customer-account/travelcards-and-tickets/voucher-tickets/voucher-tickets.component';
import { EmailSentComponent } from './containers/email-sent/email-sent.component';
import { EmptyStateComponent } from './containers/empty-state/empty-state.component';
import { ExploreFavoriteScheduleItemComponent } from './containers/explore/explore-favorite-schedule-item/explore-favorite-schedule-item.component';
import { ExploreFavoriteScheduleComponent } from './containers/explore/explore-favorite-schedule/explore-favorite-schedule.component';
import { PatternScheduleOnStopAdditionalInfoComponent } from './containers/explore/pattern-schedule-on-stop-additional-info/pattern-schedule-on-stop-additional-info.component';
import { ExploreRoutesComponent } from './containers/explore/explore-routes/explore-routes.component';
import { JourneyPlannerFavoriteListComponent } from './containers/journey-planner/journey-planner-favorite-list/journey-planner-favorite-list.component';
import { JourneyPlannerFavoritesListModalComponent } from './containers/journey-planner/journey-planner-favorites-list-modal/journey-planner-favorites-list-modal.component';
import { JourneyPlannerLocationMarkComponent } from './containers/journey-planner/journey-planner-location-mark/journey-planner-location-mark.component';
import { LoginFormComponent } from './containers/login/form/login-form.component';
import { LoginWidgetComponent } from './containers/login/main/login-widget.component';
import { LoginAsideComponent } from './containers/login/side/login-aside.component';
import { MapBusIconComponent } from './containers/map/component/icon/map-bus-icon.component';
import { MapIconComponent } from './containers/map/component/map-icon.component';
import { MapLayerGroupComponent } from './containers/map/component/map-layer.component';
import { MapLocatorComponent } from './containers/map/component/map-locator.component';
import { MapMarkerGroupComponent } from './containers/map/component/map-marker-group.component';
import { MapMarkerComponent } from './containers/map/component/map-marker.component';
import { MapPolylinePointComponent } from './containers/map/component/map-polyline-point.component';
import { MapPolylineComponent } from './containers/map/component/map-polyline.component';
import { MapPopupComponent } from './containers/map/component/map-popup.component';
import { MapComponent } from './containers/map/component/map.component';
import { LeafletApiService, MapApiService } from './containers/map/services';
import { MapSettingsDialogComponent } from './containers/map/component/map-settings-dialog/map-settings-dialog.component';
import { PaymentResultComponent } from './containers/payment-result/payment-result.component';
import { ProductCategorySelectorComponent } from './containers/product-category-selector/product-category-selector.component';
import { PurchaseHistoryComponent } from './containers/purchase-history/purchase-history.component';
import { PurchaseInvoiceComponent } from './containers/purchase-invoice/purchase-invoice.component';
import { TravelCardInputComponent } from './containers/purchase/medium/card-input/travel-card-input.component';
import { EmailInputComponent } from './containers/purchase/medium/email-input/email-input.component';
import { MediumTypeGroupSelectorComponent } from './containers/purchase/medium/medium-type-group-selector/medium-type-group-selector.component';
import { PurchaseMediumComponent } from './containers/purchase/medium/purchase-medium.component';
import { VoucherInputComponent } from './containers/purchase/medium/voucher/voucher-input.component';
import { NotificationsComponent } from './containers/purchase/notifications/notifications.component';
import { ProductSelectorComponent } from './containers/purchase/product-selector/product-selector.component';
import { PurchaseSummaryComponent } from './containers/purchase/purchase-summary/purchase-summary.component';
import { StopsZonesSelectorComponent } from './containers/purchase/stops-zones-selector/stops-zones-selector.component';
import { ValidityComponent } from './containers/purchase/validity/validity.component';
import { WalletProductSelectorComponent } from './containers/purchase/wallet-product-selector/wallet-product-selector.component';
import { ResetPasswordComponent } from './containers/recover-password-verification/form/reset-password.component';
import { ResetPasswordAsideComponent } from './containers/recover-password-verification/side/reset-password-aside.component';
import { RecoverPasswordComponent } from './containers/recover-password/form/recover-password.component';
import { RecoverPasswordAsideComponent } from './containers/recover-password/side/recover-password-aside.component';
import { SchedulePlannerTripDetailsComponent } from './containers/schedule/planner-trip-details/planner-trip-details.component';
import { SchedulePlannerComponent } from './containers/schedule/planner/planner.component';
import { ScheduleRouteDirectionComponent } from './containers/schedule/route-direction/route-direction.component';
import { RouteListComponent } from './containers/schedule/route-list/route-list.component';
import { ScheduleRouteStopDetailsComponent } from './containers/schedule/route-stop-details/route-stop-details.component';
import { RouteStopsComponent } from './containers/schedule/route-stops/route-stops.component';
import { ScheduleRouteTripComponent } from './containers/schedule/route-trip/route-trip.component';
import { ScheduleSearchComponent } from './containers/schedule/search/schedule-search.component';
import { RegisterSeutulippuComponent } from './containers/seutulippu/register-seutulippu/register-seutulippu.component';
import { CartEmptyComponent } from './containers/shopping-cart/cart-empty/cart-empty.component';
import { CartPaymentMethodsComponent } from './containers/shopping-cart/cart-payment-methods/cart-payment-methods.component';
import { CartSummaryComponent } from './containers/shopping-cart/cart-summary/cart-summary.component';
import { CartSwishComponent } from './containers/shopping-cart/cart-swish/cart-swish.component';
import { CartTermsComponent } from './containers/shopping-cart/cart-terms/cart-terms.component';
import { CartTicketsComponent } from './containers/shopping-cart/cart-tickets/cart-tickets.component';
import { NavigateAwayModalComponent } from './containers/shopping-cart/navigate-away-modal/navigate-away-modal.component';
import { RegisterAsideComponent } from './containers/signup/register-aside/register-aside.component';
import { SignUpFormComponent } from './containers/signup/form/signup-form.component';
import { SignupViispFormComponent } from './containers/signup/form/signup-viisp-form/signup-viisp-form.component';
import { SignupEmailFormComponent } from './containers/signup/form/signup-email-form/signup-email-form.component';
import { SignupEmailNoTWFormComponent } from './containers/signup/form/signup-email-notw-form/signup-email-notw-form.component';
import { SignupCompleteFormComponent } from './containers/signup/form/signup-complete-form/signup-complete-form.component';
import { SwishLoadingComponent } from './containers/swish-loading/swish-loading.component';
import { TermsComponent } from './containers/terms/terms.component';
import { ValidationHistoryComponent } from './containers/validation-history/validation-history.component';
import { MediumSelectorComponent } from './containers/purchase/medium/medium-selector/medium-selector.component';
import { ProductPassengerCountSelectorComponent } from './containers/book-ride/product-passenger-count-selector/product-passenger-count-selector.component';
import { AdyenIntegrationComponent } from './containers/shopping-cart/adyen-integration/adyen-integration.component';
import { TravelcardTicketsComponent } from './containers/travelcard-tickets/travelcard-tickets.component';
import { OndemandTripsComponent } from './containers/book-ride/ondemand-trips/ondemand-trips.component';
import { JourneyPlannerSearchComponent } from './containers/journey-planner/journey-planner-search/journey-planner-search.component';
import { JourneyPlannerSearchModalComponent } from './containers/journey-planner/journey-planner-search-modal/journey-planner-search-modal.component';
import { ExploreSearchComponent } from './containers/explore/explore-search/explore-search.component';
import { BusStopShortComponent } from './containers/explore/bus-stop-short/bus-stop-short.component';
import { DisruptionMessagesSummaryComponent } from './containers/disruption-messages/disruption-messages-summary/disruption-messages-summary.component';
import { DisruptionMessagesAccordionComponent } from './containers/disruption-messages/disruption-messages-accordion/disruption-messages-accordion.component';
import { TicketDetailsComponent } from './containers/ticket-details/ticket-details.component';
import { TicketCardComponent } from './containers/ticket-card/ticket-card.component';
import { TicketCardContainerComponent } from './containers/ticket-card-container/ticket-card-container.component';
import { TicketCardPopoverComponent } from './containers/ticket-card-popover/ticket-card-popover.component';
import { SelectFromMapModalComponent } from './containers/journey-planner/select-from-map-modal/select-from-map-modal.component';
import { TripDetailsComponent } from './containers/explore/trip-details/trip-details.component';
import { RouteShortNameComponent } from './containers/explore/route-short-name/route-short-name.component';
import { RouteNameComponent } from './containers/explore/route-name/route-name.component';
import { ExploreSearchModalComponent } from './containers/explore/explore-search-modal/explore-search-modal.component';
import { SearchRecentsComponent } from './containers/explore/search-recents/search-recents.component';
import { SearchItemComponent } from './containers/explore/search-item/search-item.component';
import { NotificationSettingsComponent } from './containers/notification-settings/notification-settings.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { GroupSelectorComponent } from './containers/purchase/group-selector/group-selector.component';
import { DepartFilterComponent } from './containers/explore/depart-filter/depart-filter.component';
import { DepartureRoutesListComponent } from './containers/explore/departure-routes-list/departure-routes-list.component';
import { TripDepartureComponent } from './containers/explore/trip-departure/trip-departure.component';
import { StopDetailsComponent } from './containers/explore/stop-details/stop-details.component';
import { BusStopHeaderComponent } from './containers/explore/bus-stop-header/bus-stop-header.component';
import { CardsListItemComponent } from './containers/customer-account/cards/cards-list-item/cards-list-item.component';
import { JourneyItineraryComponent } from './containers/journey-planner/journey-itinerary/journey-itinerary.component';
import { JourneyPriceComponent } from './containers/journey-planner/journey-price/journey-price.component';
import { ItineraryDetailsHeaderComponent } from './containers/journey-planner/itinerary-details-header/itinerary-details-header.component';
import { SmallScreenSheetModalContainerComponent } from './containers/small-screen-sheet-modal-container/small-screen-sheet-modal-container.component';
import { ItineraryLegsOverviewComponent } from './containers/journey-planner/itinerary-legs-overview/itinerary-legs-overview.component';
import { LegTripComponent } from './containers/journey-planner/itinerary-legs-overview/leg-trip/leg-trip.component';
import { LegOriginComponent } from './containers/journey-planner/itinerary-legs-overview/leg-origin/leg-origin.component';
import { LegDestinationComponent } from './containers/journey-planner/itinerary-legs-overview/leg-destination/leg-destination.component';
import { LegWalkComponent } from './containers/journey-planner/itinerary-legs-overview/leg-walk/leg-walk.component';
import { LegWaitComponent } from './containers/journey-planner/itinerary-legs-overview/leg-wait/leg-wait.component';
import { RoutePatternsComponent } from './containers/explore/route-patterns/route-patterns.component';
import { TicketActionModalComponent } from './containers/ticket-action-modal/ticket-action-modal.component';
import { MatButtonModule } from '@angular/material/button';
import { CorpCardSelectionComponent } from './containers/purchase/corporate-card-selection/corporate-card-selection.component';
import { PatternScheduleOnStopComponent } from './containers/explore/pattern-schedule-on-stop/pattern-schedule-on-stop.component';
import { ScheduleComponent } from './containers/explore/schedule/schedule.component';
import { LoginEmailFormComponent } from './containers/login/form/login-email-form/login-email-form.component';
import { LoginMobileidFormComponent } from './containers/login/form/login-mobileid-form/login-mobileid-form.component';
import { LoginSmartidFormComponent } from './containers/login/form/login-smartid-form/login-smartid-form.component';
import { LoginViispFormComponent } from './containers/login/form/login-viisp-form/login-viisp-form.component';
import { ChallengeMessageComponent } from './containers/login/form/challenge-message/challenge-message.component';
import { CardsActionsComponent } from './containers/customer-account/cards/cards-actions/cards-actions.component';
import { CardsListComponent } from './containers/customer-account/cards/cards-list/cards-list.component';
import { CustomerAccountDetailSTWComponent } from './containers/customer-account/customer-account-detail-stw/customer-account-detail-stw.component';
import { CustomerAccountDeleteComponent } from './containers/customer-account/customer-account-delete/customer-account-delete.component';
import { CardDetailsOverviewComponent } from './containers/customer-account/cards/card-details-overview/card-details-overview.component';
import { CardTicketsListComponent } from './containers/customer-account/cards/card-tickets-list/card-tickets-list.component';
import { CardContextualMenuComponent } from './containers/customer-account/cards/card-contextual-menu/card-contextual-menu.component';
import { TicketContextualMenuComponent } from './containers/customer-account/cards/ticket-contextual-menu/ticket-contextual-menu.component';
import { CardBlockModalComponent } from './containers/customer-account/cards/card-block-modal/card-block-modal.component';
import { CardRemoveModalComponent } from './containers/customer-account/cards/card-remove-modal/card-remove-modal.component';
import { WalletTopUpModalComponent } from './containers/wallet-v2/wallet-top-up-modal/wallet-top-up-modal.component';
import { EnterNewCardModalComponent } from './containers/wallet-v2/enter-new-card-modal/enter-new-card-modal.component';
import { TopUpOtherPersonCardModalComponent } from './containers/wallet-v2/top-up-other-person-card-modal/top-up-other-person-card-modal.component';

import { AddEditCardButtonComponent } from './containers/customer-account/cards/add-edit-card-button/add-edit-card-button.component';
import { AddEditCardFormComponent } from './containers/customer-account/cards/add-edit-card-form/add-edit-card-form.component';

import { DialogZoneInfoModule } from './containers/purchase/dialog-zone-info/dialog-zone-info.module';
import { OnboardingModule } from './containers/onboarding/onboarding.module';
import { FeedbackModule } from './containers/feedback/feedback.module';
import { FavoritesListModalComponent } from './containers/explore/favorites-list-modal/favorites-list-modal.component';
import { PrivacyComponent } from './containers/privacy/privacy.component';
import { TermsDialogMobileComponent } from '@ridango/components/lib/core-ui/advanced/terms-dialog-mobile/terms-dialog-mobile.component';
import { HistoryTicketCardContainerComponent } from './containers/history-ticket-card-container/history-ticket-card-container.component';
import { DisruptionMessagesFilterModalComponent } from './containers/disruption-messages/disruption-messages-filter-modal/disruption-messages-filter-modal.component';

import { CaptionComponent } from './caption/caption.component';
import { RecaptchaModule } from 'ng-recaptcha';
import { SearchRecentlyJourneyComponent } from './containers/journey-planner/search-recently-journeys/search-recently-journey/search-recently-journey.component';
import { SearchRecentlyJourneysComponent } from './containers/journey-planner/search-recently-journeys/search-recently-journeys.component';
import { ExploreLocationComponent } from './containers/explore/explore-location/explore-location.component';
import { AddFavoriteLocationModalComponent } from './containers/explore/explore-location/add-favorite-location-modal/add-favorite-location-modal.component';
import { JourneyPlannerFilterComponent } from './containers/journey-planner/journey-planner-filter/journey-planner-filter.component';
import { JourneyPlannerFilterRouteComponent } from './containers/journey-planner/journey-planner-filter/journey-planner-filter-route/journey-planner-filter-route.component';
import { JourneyPlannerFilterChipsComponent } from './containers/journey-planner/journey-planner-filter/journey-planner-filter-chips/journey-planner-filter-chips.component';
import { MatSliderModule } from '@angular/material/slider';
import { JourneyPlannerFilterTransportModeComponent } from './containers/journey-planner/journey-planner-filter/journey-planner-filter-transport-mode/journey-planner-filter-transport-mode.component';
import { JourneyPlannerFilterModalComponent } from './containers/journey-planner/journey-planner-filter-modal/journey-planner-filter-modal.component';
import { JourneyPlannerRoutesModalComponent } from './containers/journey-planner/journey-planner-routes-modal/journey-planner-routes-modal.component';
import { JourneyPlannerFilterSliderComponent } from './containers/journey-planner/journey-planner-filter/journey-planner-filter-slider/journey-planner-filter-slider.component';
import { RouteDirectionListModalComponent } from './containers/explore/route-direction-list-modal/route-direction-list-modal';
import { DisruptionMessageDetailsComponent } from './containers/journey-planner/disruption-message-details/disruption-message-details.component';
import { CustomerAccountUserAvatarComponent } from './containers/customer-account/customer-account-user-avatar/customer-account-user-avatar.component';
import { CustomerAccountDeleteDialogComponent } from './containers/customer-account/customer-account-delete-dialog/customer-account-delete-dialog.component';
import { AddFavoriteStopModalComponent } from './containers/explore/stop-details/add-favorite-stop-modal/add-favorite-stop-modal.component';
import { TicketDetailsZoneMapComponent } from './containers/ticket-details/ticket-details-zone-map/ticket-details-zone-map.component';
import { PrintScheduleService } from './services/print-schedule.service';
import { ShapeDirective } from './directives/shape.directive';
import { ProductComponent } from './containers/purchase-v2/product/product.component';
import { CategoryProductComponent } from './containers/purchase-v2/category-product/category-product.component';
import { TicketWarningModalComponent } from './containers/purchase-v2/ticket-warning-modal/ticket-warning-modal.component';
import { PermissionNotificationModalComponent } from './containers/notification-settings/permission-code-modal/permission-notification-modal.component';
import { ExploreRouteGroupsComponent } from './containers/explore/explore-route-groups/explore-route-groups.component';
import { LayoutModule } from '@angular/cdk/layout';
import { CustomerAccountChangePasswordV2Component } from './containers/customer-account/customer-account-change-password-v2/customer-account-change-password-v2.component';
import { TicketQrComponent } from './containers/ticket-details/ticket-qr/ticket-qr.component';
import { HighlightPipe } from './pipes/highlight.pipe';
import { AccountActionIconPipe } from '@ridango/components/lib/pipes/account-action-icon.pipe';
import { SmallScreenSheetModalContainerService } from './containers/small-screen-sheet-modal-container/small-screen-sheet-modal-container.service';
import { BannerComponent } from './containers/banner/banner.component';
import { BannerDetailsPageComponent } from './containers/banner-details-page/banner-details-page.component';
import { SelectPeriodLendModalComponent } from './containers/ticket-action-modal/select-period-lend-modal/select-period-lend-modal.component';
import { JourneyPlannerMapService } from './containers/map/services/journey-planner-map.service';
import { AnonymousUserModalToShoppingCartComponent } from './containers/anonymous-user-modals/anonymous-user-modal-to-shopping-cart/anonymous-user-modal-to-shopping-cart.component';
import { AnonymousUserModalComponent } from './containers/anonymous-user-modals/anonymous-user-modal/anonymous-user-modal.component';
import { TransferAnonymousDataModalComponent } from './containers/transfer-anonymous-data-modal/transfer-anonymous-data-modal.component';
import { BankcardTicketsComponent } from './containers/bankcard-tickets/bankcard-tickets.component';
import { BankcardTicketsTableComponent } from './containers/bankcard-tickets/bankcard-tickets-table/bankcard-tickets-table.component';
import { ProductQuantitySelectorComponent } from './containers/product-quantity-selector/product-quantity-selector.component';
import { BankCardsEditFormComponent } from './containers/bank-cards-edit-form/bank-cards-edit-form.component';
import { RemoveExpiredTicketModalComponent } from './containers/remove-expired-ticket-modal/remove-expired-ticket-modal.component';
import { DateCalculationService } from './services/date-calculation.service';
import { TicketActionsService } from './services/ticket-actions/ticket-actions.service';
import { AddNotificationsComponent } from './containers/add-notifications/add-notifications.component';
import { AddEmailModalComponent } from './containers/add-email-modal/add-email-modal.component';
import { EditSubscriptionsComponent } from './containers/edit-subscriptions/edit-subscriptions.component';
import { EmailModalService } from './containers/add-email-modal/email-modal.service';
import { SubscriptionsConfirmModalComponent } from './containers/add-subscriptions/subscriptions-confirm-modal/subscriptions-confirm-modal.component';
import { AddSubscriptionsComponent } from './containers/add-subscriptions/add-subscriptions.component';
import { JourneyTicketPurchaseTimeModalComponent } from './containers/journey-planner/journey-ticket-purchase-time-modal/journey-ticket-purchase-time-modal.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { JourneyPlannerFilterViaPointComponent } from './containers/journey-planner/journey-planner-filter/journey-planner-filter-via-point/journey-planner-filter-via-point.component';
import { JourneyPlannerViaPointModalComponent } from './containers/journey-planner/journey-planner-via-point-modal/journey-planner-via-point-modal.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TicketDetailsV3Component } from './containers/ticket-details-v3/ticket-details-v3.component';
import { TicketDetailsV2Component } from './containers/ticket-detail-v2/ticket-details-v2.component';
import { TicketAnimationComponent } from './containers/ticket-details-v3/ticket-animation/ticket-animation.component';
import { TicketActivateConfirmationComponent } from '@ridango/components/lib/tickets/components/ticket-activate-confirmation/ticket-activate-confirmation.component';
import { TicketActivateSameValidityConfirmationComponent } from '@ridango/components/lib/tickets/components/ticket-activate-same-validity-confirmation/ticket-activate-same-validity-confirmation.component';
import { DisruptionMessageComponent } from './containers/journey-planner/disruption-messages/disruption-message.component';
import { LegacyNotificationSettingsComponent } from './containers/notification-settings/legacy-notification-settings/legacy-notification-settings.component';

@NgModule({
    declarations: [
        AccountEmailTicketsComponent,
        AccountRelatedCardTicketsComponent,
        AccountTicketsListComponent,
        AlertComponent,
        UiInlineAlertComponent,
        UiModalAlertComponent,
        NoInternetAlertComponent,
        CartEmptyComponent,
        CartPaymentMethodsComponent,
        CartSwishComponent,
        CartSummaryComponent,
        CartTermsComponent,
        CartTicketsComponent,
        BannerDetailsPageComponent,
        AdyenIntegrationComponent,
        CookieNotificationComponent,
        CustomerAccountChangePasswordAsideComponent,
        CustomerAccountChangePasswordV2Component,
        CustomerAccountChangePasswordComponent,
        CustomerAccountDeleteSuccessComponent,
        CustomerAccountDetailAsideComponent,
        CustomerAccountDetailComponent,
        CustomerAccountDetailSTWComponent,
        CustomerAccountDeleteComponent,
        CustomerAccountEditAsideComponent,
        CustomerAccountEditComponent,
        CustomerAccountChangeEmailAsideComponent,
        CustomerAccountChangeEmailComponent,
        CustomerAccountUserAvatarComponent,
        CustomerAccountDeleteDialogComponent,
        EmailInputComponent,
        FavoritesListModalComponent,
        LoginAsideComponent,
        CardMessageRedirectComponent,
        LoginFormComponent,
        LoginWidgetComponent,
        SignupEmailFormComponent,
        SignupEmailNoTWFormComponent,
        SignupCompleteFormComponent,
        SignupViispFormComponent,
        LoginEmailFormComponent,
        LoginMobileidFormComponent,
        LoginSmartidFormComponent,
        LoginViispFormComponent,
        MapSettingsDialogComponent,
        MapComponent,
        MapComponent,
        MapLayerGroupComponent,
        MapMarkerComponent,
        MapMarkerGroupComponent,
        MapIconComponent,
        MapPopupComponent,
        MapPolylineComponent,
        MapPolylinePointComponent,
        MapBusIconComponent,
        MapLocatorComponent,
        MediumTypeGroupSelectorComponent,
        ProductCategorySelectorComponent,
        ProductQuantitySelectorComponent,
        ProductSelectorComponent,
        PurchaseHistoryComponent,
        PurchaseHistoryComponent,
        PurchaseMediumComponent,
        RecoverPasswordAsideComponent,
        RecoverPasswordComponent,
        RegisterAsideComponent,
        SignUpFormComponent,
        ExploreFavoriteScheduleComponent,
        ExploreFavoriteScheduleItemComponent,
        RelatedCardAddAndEditAsideComponent,
        RelatedCardAddAndEditComponent,
        RelatedCardDetailComponent,
        RelatedCardsEmptyStateComponent,
        RelatedCardsListHeaderComponent,
        RelatedCardsListComponent,
        ResetPasswordAsideComponent,
        ResetPasswordComponent,
        TermsComponent,
        TermsDialogMobileComponent,
        TravelCardInputComponent,
        VoucherInputComponent,
        VoucherTicketsComponent,
        WalletProductSelectorComponent,
        TermsComponent,
        ValidationHistoryComponent,
        ProductSelectorComponent,
        ProductCategorySelectorComponent,
        MediumTypeGroupSelectorComponent,
        EmailInputComponent,
        PurchaseSummaryComponent,
        EmailSentComponent,
        PaymentResultComponent,
        SwishLoadingComponent,
        RouteListComponent,
        ScheduleRouteDirectionComponent,
        PurchaseInvoiceComponent,
        ScheduleRouteStopDetailsComponent,
        RouteStopsComponent,
        ScheduleSearchComponent,
        ScheduleRouteTripComponent,
        SchedulePlannerComponent,
        SchedulePlannerTripDetailsComponent,
        PlanRideComponent,
        PassengerSelectorComponent,
        AdditionalInformationComponent,
        PayingSelectorComponent,
        BookingSummaryComponent,
        BookingTicketComponent,
        BankcardTicketsComponent,
        BankcardTicketsTableComponent,
        EmptyStateComponent,
        StopsZonesSelectorComponent,
        NavigateAwayModalComponent,
        RelatedCardPersonalizeComponent,
        RelatedCardPersonalizeAsideComponent,
        IsicCardActivationAsideComponent,
        IsicCardActivationComponent,
        RegisterSeutulippuComponent,
        MediumSelectorComponent,
        NotificationsComponent,
        ProductPassengerCountSelectorComponent,
        TravelcardTicketsComponent,
        ValidityComponent,
        OndemandTripsComponent,
        JourneyPlannerSearchComponent,
        JourneyPlannerFavoriteListComponent,
        JourneyPlannerLocationMarkComponent,
        JourneyPlannerSearchModalComponent,
        JourneyPlannerFilterComponent,
        JourneyPlannerFilterRouteComponent,
        JourneyPlannerFilterChipsComponent,
        JourneyPlannerFilterModalComponent,
        JourneyPlannerFilterTransportModeComponent,
        JourneyPlannerFilterSliderComponent,
        JourneyPlannerFavoritesListModalComponent,
        SelectFromMapModalComponent,
        ExploreSearchComponent,
        BusStopShortComponent,
        DisruptionMessagesSummaryComponent,
        DisruptionMessagesAccordionComponent,
        DisruptionMessagesFilterModalComponent,
        SelectPeriodLendModalComponent,
        TicketDetailsComponent,
        TicketDetailsV3Component,
        TicketAnimationComponent,
        TicketDetailsV2Component,
        TicketQrComponent,
        TicketDetailsZoneMapComponent,
        TicketCardComponent,
        TicketCardContainerComponent,
        HistoryTicketCardContainerComponent,
        TicketCardPopoverComponent,
        TripDetailsComponent,
        RouteShortNameComponent,
        RouteNameComponent,
        ExploreSearchModalComponent,
        SearchRecentsComponent,
        SearchItemComponent,
        NotificationSettingsComponent,
        PermissionNotificationModalComponent,
        GroupSelectorComponent,
        StopDetailsComponent,
        AddFavoriteStopModalComponent,
        BusStopHeaderComponent,
        DepartFilterComponent,
        DepartureRoutesListComponent,
        TripDepartureComponent,
        CardsActionsComponent,
        CardsListComponent,
        CardsListItemComponent,
        JourneyItineraryComponent,
        JourneyPriceComponent,
        ItineraryDetailsHeaderComponent,
        SmallScreenSheetModalContainerComponent,
        ItineraryLegsOverviewComponent,
        LegTripComponent,
        LegOriginComponent,
        LegDestinationComponent,
        LegWalkComponent,
        LegWaitComponent,
        RoutePatternsComponent,
        TicketActionModalComponent,
        TicketActivateConfirmationComponent,
        TicketActivateSameValidityConfirmationComponent,
        CorpCardSelectionComponent,
        PatternScheduleOnStopComponent,
        ScheduleComponent,
        ExploreRoutesComponent,
        ChallengeMessageComponent,
        CardsActionsComponent,
        CardsListComponent,
        CardDetailsOverviewComponent,
        CardTicketsListComponent,
        CardContextualMenuComponent,
        TicketContextualMenuComponent,
        CardBlockModalComponent,
        CardRemoveModalComponent,
        WalletTopUpModalComponent,
        EnterNewCardModalComponent,
        TopUpOtherPersonCardModalComponent,
        AddEditCardButtonComponent,
        AddEditCardFormComponent,
        CardsListItemComponent,
        PatternScheduleOnStopAdditionalInfoComponent,
        PrivacyComponent,
        CaptionComponent,
        ExploreLocationComponent,
        AddFavoriteLocationModalComponent,
        RouteDirectionListModalComponent,
        DisruptionMessageComponent,
        DisruptionMessageDetailsComponent,
        PrivacyComponent,
        ExploreRouteGroupsComponent,
        BannerComponent,
        ShapeDirective,
        HighlightPipe,
        AccountActionIconPipe,
        SearchRecentlyJourneyComponent,
        SearchRecentlyJourneysComponent,
        AnonymousUserModalComponent,
        AnonymousUserModalToShoppingCartComponent,
        TransferAnonymousDataModalComponent,
        JourneyPlannerRoutesModalComponent,
        SearchRecentlyJourneysComponent,
        ProductComponent,
        CategoryProductComponent,
        TicketWarningModalComponent,
        BankCardsEditFormComponent,
        RemoveExpiredTicketModalComponent,
        AddNotificationsComponent,
        EditSubscriptionsComponent,
        AddEmailModalComponent,
        SubscriptionsConfirmModalComponent,
        AddSubscriptionsComponent,
        JourneyTicketPurchaseTimeModalComponent,
        AddSubscriptionsComponent,
        JourneyPlannerFilterViaPointComponent,
        JourneyPlannerViaPointModalComponent,
        LegacyNotificationSettingsComponent
    ],
    providers: [
        { provide: MapApiService, useClass: LeafletApiService },
        PrintScheduleService,
        JourneyPlannerMapService,
        SmallScreenSheetModalContainerService,
        DateCalculationService,
        TicketActionsService,
        EmailModalService
    ],
    imports: [
        CommonModule,
        BrowserModule,
        RouterModule,
        CoreModule,
        ComponentsModule,
        CoreUiModule,
        TranslateModule,
        FormsModule,
        ReactiveFormsModule,
        LocalizeRouterModule.forChild([]),
        QRCodeModule,
        UiModule,
        MatCheckboxModule,
        MatSortModule,
        MatTableModule,
        MatTabsModule,
        MatInputModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatButtonToggleModule,
        IonicModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatSlideToggleModule,
        MatCardModule,
        DialogZoneInfoModule,
        RecaptchaModule,
        MatSliderModule,
        LayoutModule,
        MatProgressSpinnerModule,
        ScrollingModule
    ],
    exports: [
        AccountEmailTicketsComponent,
        AccountRelatedCardTicketsComponent,
        AccountTicketsListComponent,
        AlertComponent,
        UiInlineAlertComponent,
        UiModalAlertComponent,
        NoInternetAlertComponent,
        CartEmptyComponent,
        CartPaymentMethodsComponent,
        CartSwishComponent,
        CartSummaryComponent,
        CartTermsComponent,
        CartTicketsComponent,
        AdyenIntegrationComponent,
        CookieNotificationComponent,
        CustomerAccountChangePasswordAsideComponent,
        CustomerAccountChangePasswordV2Component,
        CustomerAccountChangePasswordComponent,
        CustomerAccountDeleteSuccessComponent,
        CustomerAccountDetailAsideComponent,
        CustomerAccountDetailComponent,
        CustomerAccountDetailSTWComponent,
        CustomerAccountDeleteComponent,
        CustomerAccountEditAsideComponent,
        CustomerAccountEditComponent,
        CustomerAccountChangeEmailAsideComponent,
        CustomerAccountChangeEmailComponent,
        CustomerAccountUserAvatarComponent,
        CustomerAccountDeleteDialogComponent,
        LoginAsideComponent,
        LoginWidgetComponent,
        SignupEmailFormComponent,
        SignupEmailNoTWFormComponent,
        SignupCompleteFormComponent,
        SignupViispFormComponent,
        LoginEmailFormComponent,
        LoginMobileidFormComponent,
        LoginViispFormComponent,
        MapComponent,
        MapComponent,
        MapLayerGroupComponent,
        MapMarkerComponent,
        MapMarkerGroupComponent,
        MapIconComponent,
        MapPopupComponent,
        MapPolylineComponent,
        MapPolylinePointComponent,
        MapBusIconComponent,
        MapLocatorComponent,
        ProductCategorySelectorComponent,
        ProductQuantitySelectorComponent,
        ProductSelectorComponent,
        PurchaseHistoryComponent,
        PurchaseHistoryComponent,
        PurchaseMediumComponent,
        BannerComponent,
        RecoverPasswordAsideComponent,
        RecoverPasswordComponent,
        RegisterAsideComponent,
        SignUpFormComponent,
        RelatedCardAddAndEditAsideComponent,
        RelatedCardAddAndEditComponent,
        RelatedCardDetailComponent,
        RelatedCardsEmptyStateComponent,
        RelatedCardsListHeaderComponent,
        RelatedCardsListComponent,
        ResetPasswordAsideComponent,
        ResetPasswordComponent,
        TermsComponent,
        ExploreFavoriteScheduleComponent,
        ExploreFavoriteScheduleItemComponent,
        ValidityComponent,
        VoucherInputComponent,
        VoucherTicketsComponent,
        WalletProductSelectorComponent,
        TermsComponent,
        ValidationHistoryComponent,
        PurchaseSummaryComponent,
        EmailSentComponent,
        PaymentResultComponent,
        SwishLoadingComponent,
        RouteListComponent,
        ScheduleSearchComponent,
        ScheduleRouteDirectionComponent,
        PurchaseInvoiceComponent,
        ScheduleRouteStopDetailsComponent,
        RouteStopsComponent,
        ScheduleRouteTripComponent,
        SchedulePlannerComponent,
        SchedulePlannerTripDetailsComponent,
        PlanRideComponent,
        ShapeDirective,
        HighlightPipe,
        AccountActionIconPipe,
        PassengerSelectorComponent,
        AdditionalInformationComponent,
        PayingSelectorComponent,
        BookingSummaryComponent,
        BookingTicketComponent,
        BankcardTicketsComponent,
        BankcardTicketsTableComponent,
        EmptyStateComponent,
        StopsZonesSelectorComponent,
        NavigateAwayModalComponent,
        RelatedCardPersonalizeComponent,
        RelatedCardPersonalizeAsideComponent,
        IsicCardActivationAsideComponent,
        IsicCardActivationComponent,
        RegisterSeutulippuComponent,
        NotificationsComponent,
        ProductPassengerCountSelectorComponent,
        TravelcardTicketsComponent,
        OndemandTripsComponent,
        JourneyPlannerSearchComponent,
        JourneyPlannerFavoriteListComponent,
        JourneyPlannerLocationMarkComponent,
        JourneyPlannerSearchModalComponent,
        JourneyPlannerFavoritesListModalComponent,
        JourneyPlannerFilterComponent,
        SelectFromMapModalComponent,
        ExploreSearchComponent,
        DisruptionMessagesSummaryComponent,
        DisruptionMessagesAccordionComponent,
        DisruptionMessagesFilterModalComponent,
        SelectPeriodLendModalComponent,
        NotificationSettingsComponent,
        PermissionNotificationModalComponent,
        TicketDetailsComponent,
        TicketDetailsV3Component,
        TicketAnimationComponent,
        TicketDetailsV2Component,
        TicketQrComponent,
        TicketDetailsZoneMapComponent,
        GroupSelectorComponent,
        TicketCardComponent,
        TicketCardContainerComponent,
        HistoryTicketCardContainerComponent,
        TicketCardPopoverComponent,
        BannerDetailsPageComponent,
        TripDetailsComponent,
        StopDetailsComponent,
        CardsActionsComponent,
        CardsListComponent,
        JourneyItineraryComponent,
        JourneyPriceComponent,
        ItineraryDetailsHeaderComponent,
        SmallScreenSheetModalContainerComponent,
        ItineraryLegsOverviewComponent,
        RoutePatternsComponent,
        CorpCardSelectionComponent,
        PatternScheduleOnStopComponent,
        CardsActionsComponent,
        CardsListComponent,
        AddEditCardButtonComponent,
        AddEditCardFormComponent,
        CardDetailsOverviewComponent,
        CardTicketsListComponent,
        CardContextualMenuComponent,
        TicketContextualMenuComponent,
        CardBlockModalComponent,
        CardRemoveModalComponent,
        WalletTopUpModalComponent,
        EnterNewCardModalComponent,
        TopUpOtherPersonCardModalComponent,
        OnboardingModule,
        FeedbackModule,
        PrivacyComponent,
        PatternScheduleOnStopAdditionalInfoComponent,
        TermsDialogMobileComponent,
        ExploreLocationComponent,
        AddFavoriteLocationModalComponent,
        DisruptionMessageComponent,
        DisruptionMessageDetailsComponent,
        TermsDialogMobileComponent,
        SearchRecentlyJourneyComponent,
        SearchRecentlyJourneysComponent,
        ExploreRoutesComponent,
        ExploreRouteGroupsComponent,
        ProductComponent,
        CategoryProductComponent,
        TicketWarningModalComponent,
        SearchRecentlyJourneysComponent,
        AnonymousUserModalComponent,
        TransferAnonymousDataModalComponent,
        JourneyPlannerRoutesModalComponent,
        BankCardsEditFormComponent,
        AddNotificationsComponent,
        EditSubscriptionsComponent,
        AddEmailModalComponent,
        SubscriptionsConfirmModalComponent,
        AddSubscriptionsComponent,
        LegacyNotificationSettingsComponent
    ]
})
export class WidgetsModule {}
